import React, { useEffect } from "react";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import { Typography } from "@progress/kendo-react-common";
import { requiredValidator } from "../../../components/formFields/CommonValidator";
import ShadowCard from "../../../components/common/ShadowCard";
import { getAllActiveTours } from "../../tour/services/tour.services";
import { clearHotelBlockRoomStatusReportList } from "../reportSlice";
import { ITour } from "../../tour/tourModel";
import moment from "moment";
import { clearTourDateList } from "../../hotelblock/hotelBlockSlice";
import { ITourDateList } from "../../hotelblock/hotelBlockModel";
import { getTourDates } from "../../hotelblock/services/hotelBlock.services";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const TourChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const dispatch = useAppDispatch();
  const isTourDateRef = React.useRef(true);
  const TourID = formRenderProps.valueGetter("TourID");

  useEffect(() => {
    if (TourID) {
      dispatch(getTourDates(TourID));
    }
    if (!isTourDateRef.current) {
      formRenderProps.onChange("TourDate", {
        value: null,
      });
    } else {
      isTourDateRef.current = false;
    }
  }, [TourID]);

  return null;
};

const HotelBlockRoomStatusReportForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.reports.loading);
  const TourList = useAppSelector((state) => state.tour.TourList);
  const TourDateList = useAppSelector((state) => state.hotelBlock.TourDateList);

  useEffect(() => {
    dispatch(getAllActiveTours());

    return () => {
      dispatch(clearHotelBlockRoomStatusReportList());
      dispatch(clearTourDateList());
    };
  }, []);

  const handleSubmit = (values: any) => {
    window.open(
      `hotelblockroomstatusview/${values?.TourID}/${
        values?.TourDate ? values?.TourDate : "0"
      }`
    );
  };

  return (
    <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
      <GridLayoutItem>
        <ShadowCard>
          <Form
            onSubmit={handleSubmit}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4
                      style={{ marginBottom: 0, marginLeft: "3px" }}
                    >
                      Hotel Block Room Status
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="TourID"
                      label="Tour"
                      placeholder="Tour"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={TourList?.map((tour: ITour) => {
                        return {
                          value: tour?.ID,
                          label: tour?.TourName,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={"TourDate"}
                      label={"Tour Date"}
                      placeholder="Tour Date"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={TourDateList?.map((tourDates: ITourDateList) => {
                        return {
                          value: `${moment(
                            tourDates?.TourDate,
                            "DD/MM/YYYY"
                          ).format("YYYY-MM-DD")}`,
                          label: tourDates?.TourDate,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      position: "relative",
                    }}
                  >
                    <div style={{ position: "absolute", top: 35, left: 0 }}>
                      <ButtonWithLoading
                        label={"Get Details"}
                        type="submit"
                        Size="medium"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </div>
                  </GridLayoutItem>
                </GridLayout>
                <TourChangeWatcher formRenderProps={formRenderProps} />
              </FormElement>
            )}
          />
        </ShadowCard>
      </GridLayoutItem>
    </GridLayout>
  );
};

export default HotelBlockRoomStatusReportForm;
